import { useEffect, useRef } from 'react';

import { QueryKey, UseQueryResult, hashKey } from '@tanstack/react-query';

/**
 * Stands in for react-query v3's `onError` option: calls `onError` once per failed fetch that completes while
 * this hook is showing `queryKey`, and never for an error that was already cached when it started showing it.
 */
const useQueryErrorEffect = (query: UseQueryResult<unknown, unknown>, queryKey: QueryKey, onError: () => void) => {
  const { isError, errorUpdatedAt, isFetchedAfterMount } = query;
  const keyHash = hashKey(queryKey);
  const attached = useRef({ hash: '', at: 0 });
  const handledErrorAt = useRef(0);

  useEffect(() => {
    if (attached.current.hash !== keyHash) {
      attached.current = { hash: keyHash, at: Date.now() };
    }
    // isFetchedAfterMount is reset by queryClient.resetQueries() (logout), so also accept errors newer than the attach
    const isNewError = isFetchedAfterMount || errorUpdatedAt > attached.current.at;
    if (!isError || !isNewError || errorUpdatedAt === handledErrorAt.current) return;
    handledErrorAt.current = errorUpdatedAt;
    onError();
  }, [isError, errorUpdatedAt, isFetchedAfterMount, keyHash, onError]);
};

export default useQueryErrorEffect;
